import { z } from 'zod';
import { TASK_ACTIVITY_KINDS } from '../enums';

/**
 * Phase 3 enums: push devices and deliveries (PLAN §3.1 `push_devices`, `push_deliveries`, amended
 * by D-504), the push preview preference (D-505), the `task_events` keys (D-514) and read receipts
 * (D-550). The database CHECK constraints are built from these lists.
 */

// ---------------------------------------------------------------- push devices

/**
 * `push_devices.platform`: `ios` and `android` are the native app; `web` is a browser or installed
 * web app with a Web Push subscription (D-750). The desktop shell (Tauri) does not register: it
 * shows notifications from the socket while it runs, like the web app in a tab.
 */
export const PUSH_PLATFORMS = ['ios', 'android', 'web'] as const;
export type PushPlatform = (typeof PUSH_PLATFORMS)[number];
export const pushPlatformSchema = z.enum(PUSH_PLATFORMS);

/**
 * `push_devices.token_kind`: `expo` for standard pushes through the Expo push service, `apns` the
 * raw APNs device token (badge-only and collapse fallback through direct APNs, §7 risk 1),
 * `apns_voip` the PushKit token (Phase 6 calls), `fcm` the raw FCM token (Android data messages),
 * `webpush` a Web Push subscription of a browser or installed web app (D-750): the token is the
 * subscription's endpoint URL and its keys sit in `push_devices.web_push_keys`.
 */
export const PUSH_TOKEN_KINDS = ['expo', 'apns', 'apns_voip', 'fcm', 'webpush'] as const;
export type PushTokenKind = (typeof PUSH_TOKEN_KINDS)[number];
export const pushTokenKindSchema = z.enum(PUSH_TOKEN_KINDS);

/**
 * The longest `token` a `webpush` device may have. Endpoints are URLs the push service chose:
 * about 200 characters at Google, Mozilla and Apple, but Windows (Edge) endpoints carry a token
 * of 500 to 700 characters. Every other kind stays within 512 (`PUSH_TOKEN_MAX_LENGTH`); the
 * database CHECK allows 2048 for `webpush` only.
 */
export const WEB_PUSH_ENDPOINT_MAX_LENGTH = 2048;

/** Base64url (no padding) lengths of a Web Push subscription's keys: P-256 point and auth secret. */
export const WEB_PUSH_P256DH_LENGTH = 87;
export const WEB_PUSH_AUTH_LENGTH = 22;
/** The VAPID public key (the `applicationServerKey`) is the same 65-byte P-256 point. */
export const WEB_PUSH_PUBLIC_KEY_LENGTH = WEB_PUSH_P256DH_LENGTH;

/** Token kinds that need `apns_environment`. */
export const APNS_TOKEN_KINDS = ['apns', 'apns_voip'] as const satisfies readonly PushTokenKind[];

/** `push_devices.apns_environment` (PLAN §3.1): the APNs host the token belongs to. */
export const APNS_ENVIRONMENTS = ['sandbox', 'production'] as const;
export type ApnsEnvironment = (typeof APNS_ENVIRONMENTS)[number];
export const apnsEnvironmentSchema = z.enum(APNS_ENVIRONMENTS);

/** `push_devices.invalidated_reason`. */
export const PUSH_INVALIDATED_REASONS = [
  /** Expo receipt `DeviceNotRegistered`. */
  'device_not_registered',
  /** APNs 410 `Unregistered` (direct APNs). */
  'apns_unregistered',
  /** FCM `UNREGISTERED` (direct FCM). */
  'unregistered',
  /** The session was revoked or expired, or the membership is no longer usable (daily pass). */
  'session_ended',
  /** The same token was registered again by another session (re-pointed rows keep history). */
  'replaced',
  /** The Web Push service answered 404 or 410: the browser dropped the subscription (D-750). */
  'webpush_gone',
] as const;
export type PushInvalidatedReason = (typeof PUSH_INVALIDATED_REASONS)[number];

// ---------------------------------------------------------------- push deliveries

/** `push_deliveries.source_kind`. `badge` and `call` rows have no notification id (D-504). */
export const PUSH_SOURCE_KINDS = ['message', 'activity', 'call', 'badge'] as const;
export type PushSourceKind = (typeof PUSH_SOURCE_KINDS)[number];

/** `push_deliveries.decision` (PLAN §3.1, D-110). */
export const PUSH_DECISIONS = [
  'sent',
  'skipped_read',
  'skipped_desktop_active',
  'skipped_acked',
  'skipped_quiet',
  'skipped_muted',
  'skipped_no_device',
  'failed',
] as const;
export type PushDecision = (typeof PUSH_DECISIONS)[number];

/**
 * `push_delivery_devices.provider`: who accepted the request for this device. `webpush` is the
 * browser vendor's push service (FCM, Mozilla autopush, Apple, WNS) reached through VAPID (D-750).
 */
export const PUSH_PROVIDER_NAMES = ['expo', 'apns', 'fcm', 'webpush'] as const;
export type PushProviderName = (typeof PUSH_PROVIDER_NAMES)[number];

/** `push_delivery_devices.status`. */
export const PUSH_DELIVERY_DEVICE_STATUSES = ['accepted', 'delivered', 'error'] as const;
export type PushDeliveryDeviceStatus = (typeof PUSH_DELIVERY_DEVICE_STATUSES)[number];

/**
 * `push_delivery_devices.received_source`: how the phone reported the arrival
 * (`POST /me/push-latency`): the Android background task, `getPresentedNotificationsAsync` on
 * the next open, or the iOS Notification Service Extension (stretch).
 */
export const PUSH_RECEIVED_SOURCES = ['task', 'presented', 'nse'] as const;
export type PushReceivedSource = (typeof PUSH_RECEIVED_SOURCES)[number];
export const pushReceivedSourceSchema = z.enum(PUSH_RECEIVED_SOURCES);

/** The fallback push waits this long for the foreground app's `notification.ack` (D-501). */
export const PUSH_FALLBACK_DELAY_MS = 15_000;

/** Android notification channels (payload `channelId`). */
export const PUSH_ANDROID_CHANNELS = ['messages', 'mentions', 'tasks'] as const;
export type PushAndroidChannel = (typeof PUSH_ANDROID_CHANNELS)[number];

// ---------------------------------------------------------------- preferences

/**
 * What a push shows on the lock screen (D-505, Q3): sender and text, the sender only, or nothing
 * ("New message"). Per person (`notification_preferences.push_preview`), NULL = the workspace
 * default `notifications.pushPreviewDefault`.
 */
export const PUSH_PREVIEWS = ['full', 'sender_only', 'none'] as const;
export type PushPreview = (typeof PUSH_PREVIEWS)[number];
export const pushPreviewSchema = z.enum(PUSH_PREVIEWS);

/**
 * The keys of `notification_preferences.task_events` (D-514): every task activity kind plus the
 * PTSTDM reminders and checklist verification. A missing key means on.
 */
export const TASK_EVENT_PREFERENCE_KEYS = [
  ...TASK_ACTIVITY_KINDS,
  'agenda_reminder',
  'report_reminder',
  'checklist_verification_needed',
] as const;
export type TaskEventPreferenceKey = (typeof TASK_EVENT_PREFERENCE_KEYS)[number];

// ---------------------------------------------------------------- read receipts (D-550)

/**
 * `messaging.readReceipts`: where the workspace shows read receipts. `dms` covers DMs and group
 * DMs; `all` adds channels up to {@link READ_RECEIPTS_MAX_CHANNEL_MEMBERS} members.
 */
export const READ_RECEIPTS_SETTINGS = ['off', 'dms', 'all'] as const;
export type ReadReceiptsSetting = (typeof READ_RECEIPTS_SETTINGS)[number];
export const readReceiptsSettingSchema = z.enum(READ_RECEIPTS_SETTINGS);

/** Channels with more active members never show receipts, whatever the setting. */
export const READ_RECEIPTS_MAX_CHANNEL_MEMBERS = 200;

/** Why `GET /conversations/:id/read-receipts` answers `{ items: [], disabled }`. */
export const READ_RECEIPTS_DISABLED_REASONS = [
  /** `messaging.readReceipts = off`. */
  'workspace_off',
  /** The setting does not cover this kind (a channel under `dms`, or a self DM). */
  'conversation_kind',
  /** A channel above {@link READ_RECEIPTS_MAX_CHANNEL_MEMBERS} active members. */
  'too_many_members',
  /** The caller turned `shareReadReceipts` off (reciprocity: they see no one's receipts). */
  'not_sharing',
] as const;
export type ReadReceiptsDisabledReason = (typeof READ_RECEIPTS_DISABLED_REASONS)[number];
